import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getOrderByOrderNumber } from '../../../services/adminApi';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent
} from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { updateOrderStatus } from '../../../services/adminApi';
import { toast } from 'react-toastify';

const AdminOrderDetailsPage = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (order) {
      setStatus(order.orderStatus);
    }
  }, [order]);

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await updateOrderStatus(order._id, newStatus);
      if (response.success) {
        setOrder(response.order);
        toast.success('Order status updated');
      } else {
        toast.error('Failed to update order status');
      }
    } catch (err) {
      toast.error('An error occurred while updating order status');
    }
  };

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await getOrderByOrderNumber(orderNumber);
        if (response.success) {
          setOrder(response.order);
        } else {
          setError(response.error || 'Failed to fetch order details.');
        }
      } catch (err) {
        setError('An error occurred while fetching order details.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderNumber]);

  if (loading) {
    return <p>Loading order details...</p>;
  }

  if (error) {
    return <p className="text-red-500">{error}</p>;
  }

  if (!order) {
    return <p>Order not found.</p>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-semibold mb-6">Order Details</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Order #{order.orderNumber}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <div className="flex items-center gap-2">
                    <Select value={status} onValueChange={handleStatusChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Change status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Processing">Processing</SelectItem>
                        <SelectItem value="Shipped">Shipped</SelectItem>
                        <SelectItem value="Delivered">Delivered</SelectItem>
                        <SelectItem value="Returned">Returned</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                        <SelectItem value="Refunded">Refunded</SelectItem>
                      </SelectContent>
                    </Select>
                    <Badge variant={
                      order.orderStatus === 'Delivered' ? 'success' :
                      order.orderStatus === 'Processing' ? 'warning' :
                      order.orderStatus === 'Cancelled' ? 'destructive' :
                      order.orderStatus === 'Refunded' ? 'destructive' :
                      order.orderStatus === 'Returned' ? 'destructive' :
                      'default'
                    }>{order.orderStatus}</Badge>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p>{new Date(order.createdAt).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total</p>
                  <p>£{order.totalPrice.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Method</p>
                  <p>{order.paymentMethod}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Items</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="divide-y divide-gray-200">
                {order.orderItems.map(item => (
                  <li key={item._id} className="flex items-center justify-between py-4">
                    <div className="flex items-center">
                      <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded-md mr-4" />
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-gray-500">Size: {item.size}</p>
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      </div>
                    </div>
                    <p>£{item.price.toFixed(2)}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Customer</CardTitle>
            </CardHeader>
            <CardContent>
              <p><strong>{order.user.firstName} {order.user.lastName}</strong></p>
              <p className="text-sm text-gray-500">{order.user.email}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Shipping Address</CardTitle>
            </CardHeader>
            <CardContent>
              <p>{order.shippingAddress.addressLine1}</p>
              {order.shippingAddress.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
              <p>{order.shippingAddress.townCity}, {order.shippingAddress.postcode}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Price Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Subtotal</span>
                  <span>£{order.orderItems.reduce((acc, item) => acc + item.price * item.quantity, 0).toFixed(2)}</span>
                </div>
                {order.couponDiscount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Coupon Discount ({order.coupon?.code})</span>
                    <span>- £{order.couponDiscount.toFixed(2)}</span>
                  </div>
                )}
                {order.giftCardDiscount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Gift Card Discount ({order.giftCard?.code})</span>
                    <span>- £{order.giftCardDiscount.toFixed(2)}</span>
                  </div>
                )}
                {order.madCoinsDiscount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">MAD Coins Discount</span>
                    <span>- £{order.madCoinsDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Shipping</span>
                  <span>£{order.shippingPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>£{order.totalPrice.toFixed(2)}</span>
                </div>
                {order.coinTransaction && order.coinTransaction.amount > 0 && (
                  <div className="flex justify-between mt-2 pt-2 border-t">
                    <span className="text-gray-500">MadCoins Earned</span>
                    <span className="flex items-center">
                      {order.coinTransaction.amount.toFixed(2)}
                      <img src="/images/madicon.png" alt="MAD Coin" className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDetailsPage;
